import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { darkTheme } from '../../../lib/tokens';
import type { QuizSummary, WeeklyScoreEntry } from './api';

interface Props {
  weekly: WeeklyScoreEntry[];
  summary: QuizSummary;
  weeks: number;
}

// Wochen-Quiz-Trend: Line zeigt Score-%, Bars zeigen Quiz-Volumen pro Woche.
// Zusatzblock daneben mit Gesamt-Summary (Total, Quote, Ø-Zeit).

export function QuizTrendChart({ weekly, summary, weeks }: Props): JSX.Element {
  const chartData = weekly.map((w) => ({
    week: w.week.slice(5),
    score: w.avgScore,
    count: w.quizCount,
  }));

  const hasAny = weekly.some((w) => w.quizCount > 0);

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-text-primary">
          Quiz-Trend ({weeks} Wochen)
        </h3>
        <SummaryStrip summary={summary} />
      </div>

      {!hasAny ? (
        <p className="text-xs text-text-tertiary">Noch keine Quiz-Antworten in diesem Zeitraum.</p>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={darkTheme.colors.borderSubtle} />
            <XAxis
              dataKey="week"
              stroke={darkTheme.colors.textTertiary}
              fontSize={10}
            />
            <YAxis
              yAxisId="score"
              domain={[0, 100]}
              stroke={darkTheme.colors.textTertiary}
              fontSize={10}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis
              yAxisId="count"
              orientation="right"
              stroke={darkTheme.colors.textTertiary}
              fontSize={10}
              allowDecimals={false}
            />
            <Tooltip
              contentStyle={{
                background: darkTheme.colors.bgElevated,
                border: `1px solid ${darkTheme.colors.borderSubtle}`,
                fontSize: 12,
              }}
              labelStyle={{ color: darkTheme.colors.textSecondary }}
              formatter={(value: number, key) => {
                if (key === 'score') return [`${value}%`, 'Quote'];
                return [value, 'Anzahl'];
              }}
            />
            <Bar
              yAxisId="count"
              dataKey="count"
              fill={darkTheme.colors.accentSubtle}
              radius={[2, 2, 0, 0]}
            />
            <Line
              yAxisId="score"
              type="monotone"
              dataKey="score"
              stroke={darkTheme.colors.accent}
              strokeWidth={2}
              dot={{ r: 3, fill: darkTheme.colors.accent }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function SummaryStrip({ summary }: { summary: QuizSummary }): JSX.Element {
  return (
    <div className="flex gap-4 text-[11px] text-text-tertiary">
      <span>
        <span className="font-semibold text-text-primary">{summary.total}</span> Antworten
      </span>
      <span>
        Quote:{' '}
        <span className="font-semibold text-text-primary">
          {summary.total > 0 ? `${Math.round(summary.correctRate * 100)}%` : '—'}
        </span>
      </span>
      <span>
        Ø-Zeit:{' '}
        <span className="font-semibold text-text-primary">
          {summary.avgTimeSeconds != null ? `${summary.avgTimeSeconds}s` : '—'}
        </span>
      </span>
    </div>
  );
}
